/**
 * Pure classifier for /api/* failure responses — the single place that
 * decides whether a response means "session expired", "server timed out",
 * or an ordinary error, and turns it into a safe German message.
 *
 * Why this exists: SWA's built-in auth answers an expired-session /api/*
 * call with ITS OWN 401 HTML page ("Azure Static Web Apps - 401:
 * Unauthorized …"), not JSON — that HTML must never reach `ApiError.message`
 * / the UI (owner screenshot 18.09.2026 on /schulungen). A gateway timeout
 * (502/504 — the actual SWA-gateway-abort codes) is handled the same way,
 * fail-safe on status alone since no real gateway-timeout response body is
 * captured in this repo (see the session-expired-handling handover map,
 * item 6). 403 and 503 are deliberately NOT blanket-classified: backend
 * route handlers legitimately use 403 for a real permission denial
 * (requirePermission()) and 503 for application states like
 * AI_NOT_CONFIGURED, both with a normal `{error,code}` JSON body that must
 * reach the caller unchanged, not get swallowed into the session/timeout
 * dialog (review findings, 2026-09-18).
 *
 * `/api/staff/*` is carved out of the session_expired classification: a
 * kitchen/dishwashing worker has no Entra session, so a 401 there is the
 * normal "not logged in yet" state (staffApi.ts's fetchStaffMe), not an
 * expired session — see the handover map, item 3.
 *
 * Used by both apps/intranet/src/shared/api.ts (apiFetch) and the global
 * window.fetch interceptor in main.tsx, so the decision lives in one place.
 *
 * @typedef {'session_expired' | 'timeout'} ResponseKind
 * @typedef {'session_expired' | 'timeout' | 'generic'} ClassifiedErrorKind
 * @typedef {{ kind: ClassifiedErrorKind, message: string, code?: string }} ClassifiedError
 */

export const SESSION_EXPIRED_MESSAGE = 'Sitzung abgelaufen — bitte neu anmelden.'
export const TIMEOUT_MESSAGE = 'Server antwortet nicht (Zeitüberschreitung) — bitte erneut versuchen.'
const GENERIC_MESSAGE = 'Anfrage fehlgeschlagen — bitte erneut versuchen.'

const GATEWAY_TIMEOUT_STATUSES = new Set([502, 504])

/**
 * Decides whether a response represents an expired session or a gateway
 * timeout — the shared decision behind both the classifier below and the
 * main.tsx fetch interceptor. Returns null for anything else (ordinary
 * success/error responses, which callers handle themselves).
 *
 * @param {{ path: string, status: number, contentType?: string | null }} input
 * @returns {ResponseKind | null}
 */
export function classifyResponseKind({ path, status, contentType }) {
  const isHtml = (contentType || '').toLowerCase().startsWith('text/html')
  const isApiPath = /^\/api\//.test(path || '')
  const isStaffPath = /^\/api\/staff\//.test(path || '')

  if (!isApiPath || isStaffPath) return null

  // 401 = abgelaufene Anmeldung (SWA liefert dazu eine eigene HTML-Seite).
  // 403 gehoert NICHT dazu — das ist requirePermission() mit echtem
  // {error,code}-Body fuer einen angemeldeten Nutzer.
  if (status === 401) return 'session_expired'

  // Gateway-Abbruch. Unser Backend antwortet IMMER mit JSON; eine HTML-Seite
  // mit Fehlerstatus stammt vom SWA-Proxy, der nach ~45-90 s aufgibt,
  // waehrend die Funktion weiterlaeuft (ImportWizardPage pollt genau dafuer).
  // Vorfall 18.09.2026: ein WINfOOD-Upload mit 184 Dateien bekam diese Seite,
  // die fruehere Regel "jedes HTML = Sitzung abgelaufen" oeffnete den
  // Anmelde-Dialog und brach die Polling-Rettung des Assistenten ab.
  if (GATEWAY_TIMEOUT_STATUSES.has(status) || (isHtml && status >= 500)) return 'timeout'

  // HTML mit Erfolgsstatus = fetch ist der Umleitung auf die Anmeldeseite
  // gefolgt. Das ist die abgelaufene Anmeldung ohne 401.
  if (isHtml && status < 400) return 'session_expired'

  // Andere HTML-Fehler (etwa 413 "zu gross") sind weder Anmeldung noch
  // Zeitueberschreitung. Der Aufrufer behandelt sie; classifyApiError()
  // unten sorgt dafuer, dass kein HTML-Text in die Oberflaeche gelangt.
  return null
}

/**
 * Full classification for apiFetch()'s error path: same decision as
 * classifyResponseKind(), plus the existing {error,code} JSON-unwrap
 * fallback for everything else. Never returns the raw body text.
 *
 * @param {{ path: string, status: number, contentType?: string | null, bodyText?: string | null }} input
 * @returns {ClassifiedError}
 */
export function classifyApiError({ path, status, contentType, bodyText }) {
  const kind = classifyResponseKind({ path, status, contentType })
  if (kind === 'session_expired') {
    return { kind, message: SESSION_EXPIRED_MESSAGE, code: 'session_expired' }
  }
  if (kind === 'timeout') {
    return { kind, message: TIMEOUT_MESSAGE }
  }

  if ((contentType || '').toLowerCase().startsWith('text/html')) {
    return { kind: 'generic', message: GENERIC_MESSAGE }
  }

  if (bodyText) {
    try {
      const parsed = JSON.parse(bodyText)
      if (typeof parsed?.error === 'string') {
        return { kind: 'generic', message: parsed.error, code: typeof parsed?.code === 'string' ? parsed.code : undefined }
      }
    } catch {
      // plain-text body, not JSON — fall through to the generic message
    }
  }

  return { kind: 'generic', message: GENERIC_MESSAGE }
}
